import React, { useMemo } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";

import NotFound from "components/Texts/NotFound";
import TablePagination from "./table-pagination";
import TableHeader from "./table-header";

import { Box, Grid, Typography } from "@material-ui/core";

export default function TableComponent({
  keyField,
  dataTable = [],
  columnModel,
  headerTitle,
  csvFileName,
  pagination,
  bootstrapTableProps,
  showPrint = false,
  showSearch = false,
  CustomHeader,
  searched = true,
  allowEmpty = false,
  notFoundTitle = "Nenhum dado encontrado",
  notFoundSubtitle = "Não há dados para exibir.",
  noResultTitle = "Nenhum resultado encontrado",
  noResultSubtitle = "Tente ajustar os filtros de busca.",
  hasFooter = false,
  extraActions,
  searchFormatted = true,
  styledRow,
  changeSearchPlaceholder = false,
  onRowClick,
  reshearch,
}) {
  const componentRef = React.useRef(null);
  const selectRef = React.useRef(null);

  const validDataTable = useMemo(() => {
    return Array.isArray(dataTable) ? dataTable : [];
  }, [dataTable]);

  const csvConfig =
    csvFileName && validDataTable.length > 0
      ? {
          fileName: csvFileName,
          separator: ";",
          blobType: "text/csv;charset=utf-8",
          noAutoBOM: false,
        }
      : false;

  const paginationConfig = useMemo(
    () =>
      TablePagination({
        paginationOptions: pagination,
        dataLength: validDataTable.length,
        selectRef: selectRef,
      }),
    [pagination, validDataTable.length]
  );

  const hasData = validDataTable && validDataTable.length > 0;
  const shouldShowNotFound = !hasData && !allowEmpty;
  const shouldShowNoResults = searched && !hasData;

  const rowEvents = useMemo(
    () => (onRowClick ? { onClick: onRowClick } : null),
    [onRowClick]
  );

  return (
    <>
      {searched &&
        (shouldShowNotFound ? (
          <NotFound title={notFoundTitle} subtitle={notFoundSubtitle} />
        ) : (
          <ToolkitProvider
            data={validDataTable}
            keyField={keyField}
            columns={columnModel}
            search={showSearch ? { searchFormatted } : false}
            exportCSV={csvConfig}
          >
            {(props) => {
              const safeCsvProps =
                props.csvProps && validDataTable.length > 0
                  ? {
                      ...props.csvProps,
                      onExport: () => {
                        if (
                          validDataTable &&
                          Array.isArray(validDataTable) &&
                          validDataTable.length > 0
                        ) {
                          props.csvProps.onExport();
                        }
                      },
                    }
                  : null;

              return (
                <>
                  {CustomHeader ? (
                    <CustomHeader
                      searchProps={props.searchProps}
                      csvProps={safeCsvProps}
                      componentRefProps={componentRef}
                      selectRefProps={selectRef}
                    />
                  ) : (
                    <TableHeader
                      changeSearchPlaceholder={changeSearchPlaceholder}
                      componentRef={componentRef}
                      csvFileName={csvFileName}
                      csvProps={safeCsvProps}
                      dataTable={validDataTable}
                      searchProps={props.searchProps}
                      selectRef={selectRef}
                      showPrint={showPrint}
                      showSearch={showSearch}
                      headerTitle={headerTitle}
                      extraActions={extraActions}
                      reshearch={reshearch}
                    />
                  )}

                  <Grid item xs={12}>
                    {shouldShowNoResults ? (
                      <NotFound
                        title={noResultTitle}
                        subtitle={noResultSubtitle}
                      />
                    ) : (
                      <BootstrapTable
                        ref={componentRef}
                        {...props.baseProps}
                        bootstrap4
                        bordered={false}
                        condensed
                        striped
                        hover
                        rowStyle={styledRow}
                        rowEvents={rowEvents}
                        footerClasses={
                          hasFooter ? "footer-absolute" : undefined
                        }
                        classes={hasFooter ? "table-with-footer" : undefined}
                        wrapperClasses="table-responsive"
                        headerClasses="thead-light"
                        pagination={paginationConfig}
                        noDataIndication={() => (
                          <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body2" color="textSecondary">
                              {noResultTitle}
                            </Typography>
                          </Box>
                        )}
                        {...bootstrapTableProps}
                      />
                    )}
                  </Grid>
                </>
              );
            }}
          </ToolkitProvider>
        ))}
    </>
  );
}
